// FactoryLineStories — the Stories tab of a factory line: its runs (stories)
// with "지금 실행" and an expandable per-run StoryReview.

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Play, Trash2 } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { FactoryConfig, FactoryRun, FactoryStage } from '@/api/types'
import { formatTime } from '@/lib/utils'
import { Badge, Button, Card, EmptyState } from '@/admin-design/_common'
import StoryReview from './StoryReview'

const STAGE_VARIANT: Record<
  FactoryStage,
  'info' | 'success' | 'warning' | 'muted' | 'destructive'
> = {
  generating_script: 'muted',
  generating_story: 'info',
  awaiting_review: 'warning',
  awaiting_render: 'warning',
  rendering: 'info',
  awaiting_merge: 'warning',
  done: 'success',
  error: 'destructive',
  cancelled: 'muted',
}

export default function FactoryLineStories({ config }: { config: FactoryConfig }) {
  const qc = useQueryClient()
  // Expanded story lives in the URL (?run=) for a shareable permalink.
  const [searchParams, setSearchParams] = useSearchParams()
  const runParam = searchParams.get('run')
  const expanded = runParam ? Number(runParam) : null
  const setExpanded = (id: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id == null) next.delete('run')
        else next.set('run', String(id))
        return next
      },
      { replace: true },
    )
  }
  const [runError, setRunError] = useState<string | null>(null)

  const runsQ = useQuery({
    queryKey: ['factory-runs', config.id],
    queryFn: () => api.listFactoryRuns(config.id),
    refetchInterval: 5000,
  })

  const runNow = useMutation({
    mutationFn: (topic: string) => api.runFactoryNow(config.id, topic),
    onSuccess: () => {
      setRunError(null)
      qc.invalidateQueries({ queryKey: ['factory-runs', config.id] })
    },
    onError: (e) => setRunError(errorMessage(e)),
  })

  const deleteRun = useMutation({
    mutationFn: (id: number) => api.deleteFactoryRun(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['factory-runs', config.id] }),
  })

  const runs = runsQ.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-card-foreground">스토리 ({runs.length})</span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="ml-auto"
          loading={runNow.isPending}
          onClick={() => {
            const kw = window.prompt('토픽 키워드 (비우면 최근 트렌드에서 자동 선택)')
            if (kw === null) return
            runNow.mutate(kw.trim())
          }}
        >
          <Play size={13} /> 지금 실행
        </Button>
      </div>

      {runError && <p className="text-xs text-destructive">지금 실행 실패: {runError}</p>}

      <Card>
        <div className="p-2">
          {runs.length === 0 ? (
            <EmptyState
              title="아직 스토리가 없어요."
              description="'지금 실행'으로 첫 스토리를 만들어보세요."
            />
          ) : (
            <ul className="space-y-1">
              {runs.map((r) => (
                <li key={r.id}>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted/40"
                    >
                      {expanded === r.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      <Badge variant={STAGE_VARIANT[r.stage]} className="rounded text-[10px]">
                        {r.stage}
                      </Badge>
                      <RunTitle run={r} />
                      <span className="ml-auto shrink-0 text-muted-foreground">
                        {formatTime(r.created_at)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm('이 스토리를 삭제할까요?')) return
                        deleteRun.mutate(r.id)
                      }}
                      disabled={deleteRun.isPending}
                      title="스토리 삭제"
                      aria-label="스토리 삭제"
                      className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  {expanded === r.id && (
                    <div className="px-2 pb-2">
                      <StoryReview run={r} />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  )
}

// RunTitle shows the story's real title (shooting-plan title / concept) rather
// than the raw topic_id (e.g. "781346" is a trend-collector topic id, not a
// title). Falls back to the manual keyword or "토픽 #id" while generating.
function RunTitle({ run }: { run: FactoryRun }) {
  const storyQ = useQuery({
    queryKey: ['story', run.story_id],
    queryFn: () => api.getStory(run.story_id as number),
    enabled: Boolean(run.story_id),
  })
  const manual = run.topic_id.startsWith('manual:') ? run.topic_id.slice(7) : ''
  const title =
    storyQ.data?.shooting_plan?.title ||
    storyQ.data?.concept ||
    manual ||
    (run.topic_id ? `토픽 #${run.topic_id}` : '—')
  return <span className="min-w-0 flex-1 truncate text-card-foreground">{title}</span>
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'unknown error'
}
